import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { colors, fontSizes, space } from 'style/config';
import RecapItem from 'components/Content/Recap/RecapItem';
import RecapImage from 'components/Content/Recap/RecapImage';
import { recordClicked, PREFIX_CARD_DEALS } from 'utils/puwyloSunset/sunsetHelper';
import puwyloHomepageUtils from 'utils/PuwyloHomepageUtils';
import { showAddPercentOffFlagToSalePageSelector } from 'viewModel/selectors/testTarget/showAddPercentOffFlagToSalePageSelector';

const THUMBNAIL_SIZE = [122, 166];

function RecapDeals({ sku, ...props }) {
    const showPercentOffFlag = useSelector(showAddPercentOffFlagToSalePageSelector);
    const { shouldShow: shouldShowPercentOff, label: percentOffLabel } = puwyloHomepageUtils.getPuwyloPercentOffBadge(
        sku,
        showPercentOffFlag
    );

    // A Deals card only exists for an on-sale SKU; no sku or no markdown => nothing renders.
    if (!sku || !sku.salePrice) {
        return null;
    }

    const handleTriggerClick = async sid => {
        // Record the click for PDE-2621 sunset gating before delegating to any generic hook.
        recordClicked(sku.skuId, PREFIX_CARD_DEALS);
        props.triggerClick && (await props.triggerClick(sid));
    };

    const priceAriaLabel = puwyloHomepageUtils.getPuwyloDealPriceAriaLabel(sku, shouldShowPercentOff ? percentOffLabel : '');

    return (
        <RecapItem
            {...props}
            isSingleImage={true}
            triggerClick={handleTriggerClick}
        >
            <RecapImage
                sku={sku}
                src={sku.image}
                size={THUMBNAIL_SIZE}
                showPlaceholderOnError={true}
                hideBadge={false}
            />
            <div css={styles.priceContainer}>
                <div
                    css={styles.priceRow}
                    aria-label={priceAriaLabel}
                >
                    <ins
                        css={styles.salePrice}
                        children={sku.salePrice}
                    />{' '}
                    <del
                        css={styles.listPrice}
                        children={sku.listPrice}
                    />
                    {shouldShowPercentOff && (
                        <span
                            css={styles.percentOff}
                            children={percentOffLabel}
                        />
                    )}
                </div>

                {sku.valuePrice && (
                    <div
                        css={styles.valuePrice}
                        children={sku.valuePrice}
                    />
                )}
            </div>
        </RecapItem>
    );
}

const styles = {
    priceContainer: {
        marginTop: space[2]
    },
    priceRow: {
        fontWeight: 'var(--font-weight-bold)'
    },
    valuePrice: {
        marginTop: space[1],
        color: colors.gray,
        fontSize: fontSizes.sm,
        fontWeight: 'var(--font-weight-normal)'
    },
    listPrice: {
        display: 'inline',
        color: colors.gray,
        fontWeight: 'var(--font-weight-normal)',
        textDecoration: 'line-through'
    },
    salePrice: {
        display: 'inline',
        color: colors.red,
        textDecoration: 'none'
    },
    percentOff: {
        display: 'inline-block',
        marginLeft: space[1],
        padding: '0 .4em',
        borderRadius: 2,
        backgroundColor: colors.red,
        color: colors.white,
        fontSize: fontSizes.xs,
        fontWeight: 'var(--font-weight-bold)',
        textDecoration: 'none',
        verticalAlign: 'middle'
    }
};

RecapDeals.propTypes = {
    sku: PropTypes.object,
    title: PropTypes.string,
    targetUrl: PropTypes.string,
    sid: PropTypes.string,
    isLoading: PropTypes.bool,
    triggerClick: PropTypes.func
};

export default wrapFunctionalComponent(RecapDeals, 'RecapDeals');
